/** @format */

import React from "react";
import { Box, Typography, Button, Container } from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon from "@mui/icons-material/Check";
import { colors } from "../../utility/types/constant";

const Section = styled("section")({
    background: "#f8f9fb",
    padding: "80px 0 88px",
    position: "relative",
    overflow: "hidden",
});

const Card = styled("div")(({ theme }) => ({
    background: "linear-gradient(135deg, #0d1636 0%, #1a2a6c 55%, #1e3094 100%)",
    borderRadius: "20px",
    padding: "52px 56px",
    position: "relative",
    overflow: "hidden",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: "40px",
    "&::before": {
        content: '""',
        position: "absolute",
        top: "-60px",
        right: "-60px",
        width: "320px",
        height: "320px",
        borderRadius: "50%",
        background: "radial-gradient(circle, rgba(255,204,0,0.12) 0%, transparent 70%)",
        pointerEvents: "none",
    },
    "&::after": {
        content: '""',
        position: "absolute",
        bottom: "-80px",
        left: "-40px",
        width: "280px",
        height: "280px",
        borderRadius: "50%",
        background: "radial-gradient(circle, rgba(30,193,149,0.07) 0%, transparent 70%)",
        pointerEvents: "none",
    },
    [theme.breakpoints.down("md")]: {
        flexDirection: "column",
        padding: "40px 32px",
        textAlign: "center",
    },
    [theme.breakpoints.down("sm")]: {
        padding: "32px 24px",
        borderRadius: "16px",
    },
}));

const GoldBadge = styled("span")({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: "rgba(255, 204, 0, 0.15)",
    color: "#ffcc00",
    border: "1px solid rgba(255, 204, 0, 0.28)",
    borderRadius: "100px",
    padding: "4px 13px",
    fontSize: "0.67rem",
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 700,
    letterSpacing: "0.09em",
    textTransform: "uppercase",
    marginBottom: "14px",
});

const Headline = styled(Typography)({
    fontFamily: "'Montserrat', sans-serif",
    fontSize: "clamp(1.5rem, 3.5vw, 2.1rem)",
    fontWeight: 800,
    color: "#fff",
    lineHeight: 1.15,
    letterSpacing: "-0.02em",
    marginBottom: "12px",
});

const GoldSpan = styled("span")({
    background: "linear-gradient(135deg, #ffcc00 0%, #ffaa00 100%)",
    WebkitBackgroundClip: "text",
    WebkitTextFillColor: "transparent",
    backgroundClip: "text",
});

const SubText = styled(Typography)({
    fontFamily: "'Inter', sans-serif",
    fontSize: "0.97rem",
    color: "rgba(255,255,255,0.62)",
    lineHeight: 1.7,
    maxWidth: "480px",
    marginBottom: "24px",
});

const FeatureList = styled("ul")({
    listStyle: "none",
    padding: 0,
    margin: "0 0 32px 0",
    display: "flex",
    flexDirection: "column",
    gap: "8px",
});

const FeatureItem = styled("li")({
    display: "flex",
    alignItems: "center",
    gap: "10px",
    fontFamily: "'Inter', sans-serif",
    fontSize: "0.9rem",
    color: "rgba(255,255,255,0.80)",
    fontWeight: 500,
});

const CheckCircle = styled("span")({
    width: "20px",
    height: "20px",
    minWidth: "20px",
    borderRadius: "50%",
    background: "rgba(30,193,149,0.20)",
    border: "1px solid rgba(30,193,149,0.40)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
});

const SurveyBtn = styled(Button)({
    fontFamily: "'Montserrat', sans-serif",
    fontWeight: 700,
    borderRadius: "10px",
    padding: "13px 28px",
    textTransform: "none",
    fontSize: "0.95rem",
    background: "linear-gradient(135deg, #ffcc00 0%, #ffaa00 100%)",
    color: "#0d1636",
    boxShadow: "0 4px 18px rgba(255,204,0,0.35)",
    "&:hover": {
        background: "linear-gradient(135deg, #ffe033 0%, #ffbb00 100%)",
        boxShadow: "0 6px 24px rgba(255,204,0,0.45)",
        transform: "translateY(-1px)",
    },
    transition: "all 0.2s ease",
});

const IllustrationBox = styled("div")(({ theme }) => ({
    flexShrink: 0,
    width: "240px",
    [theme.breakpoints.down("lg")]: { width: "200px" },
    [theme.breakpoints.down("md")]: { display: "none" },
}));

const StatPill = styled("div")({
    background: "rgba(255,255,255,0.07)",
    border: "1px solid rgba(255,255,255,0.10)",
    borderRadius: "12px",
    padding: "14px 18px",
    marginBottom: "12px",
    textAlign: "center",
});

const FEATURES = [
    "Automated payment reminders via SMS & email",
    "AI-powered follow-up sequences",
    "Smart payment links & installment plans",
    "Collections dashboard with payment predictions",
];

const STATS = [
    { value: "8+ hrs", label: "avg. wasted/month on follow-ups" },
    { value: "23%", label: "of invoices go unpaid past 30 days" },
    { value: "2.5×", label: "faster collection with automation" },
];

const BusinessTeaser: React.FC = () => {
    const navigate = useNavigate();

    return (
        <Section>
            {/* Section label above card */}
            <Container maxWidth="lg">
                <Box sx={{ mb: 3, textAlign: "center" }}>
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: "0.72rem",
                            fontWeight: 700,
                            letterSpacing: "0.12em",
                            textTransform: "uppercase",
                            color: "#9ca3af",
                            mb: 1,
                        }}
                    >
                        Also from FundsyFi
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: "'Montserrat', sans-serif",
                            fontSize: "clamp(1.1rem, 2.5vw, 1.4rem)",
                            fontWeight: 800,
                            color: colors.primary,
                        }}
                    >
                        Are you a business owner?
                    </Typography>
                </Box>

                <Card>
                    {/* Left: copy */}
                    <Box sx={{ flex: 1, position: "relative", zIndex: 1 }}>
                        <GoldBadge>
                            <AutoAwesomeIcon sx={{ fontSize: "0.8rem" }} />
                            FundsyFi Business · Coming Soon
                        </GoldBadge>

                        <Headline>
                            Still spending time chasing
                            <br />
                            <GoldSpan>unpaid invoices?</GoldSpan>
                        </Headline>

                        <SubText>
                            We're building an AI-powered collections and invoice
                            recovery platform for small businesses. Automated
                            reminders, smart follow-ups, and payment insights
                            so you get paid without the awkward calls.
                        </SubText>

                        <FeatureList>
                            {FEATURES.map((f) => (
                                <FeatureItem key={f}>
                                    <CheckCircle>
                                        <CheckIcon
                                            sx={{
                                                fontSize: "0.75rem",
                                                color: colors.mainSecondary,
                                            }}
                                        />
                                    </CheckCircle>
                                    {f}
                                </FeatureItem>
                            ))}
                        </FeatureList>

                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                            <SurveyBtn
                                endIcon={<ArrowForwardIcon />}
                                onClick={() => navigate("/business/survey")}
                            >
                                Join Early Access
                            </SurveyBtn>
                            <Typography
                                sx={{
                                    fontFamily: "'Inter', sans-serif",
                                    fontSize: "0.78rem",
                                    color: "rgba(255,255,255,0.42)",
                                    lineHeight: 1.5,
                                }}
                            >
                                Takes under 2 minutes.
                                <br />
                                Free. No commitment.
                            </Typography>
                        </Box>
                    </Box>

                    {/* Right: stats illustration */}
                    <IllustrationBox sx={{ position: "relative", zIndex: 1 }}>
                        {STATS.map((s) => (
                            <StatPill key={s.value}>
                                <Typography
                                    sx={{
                                        fontFamily: "'Montserrat', sans-serif",
                                        fontWeight: 800,
                                        fontSize: "1.6rem",
                                        color: "#ffcc00",
                                        lineHeight: 1.1,
                                    }}
                                >
                                    {s.value}
                                </Typography>
                                <Typography
                                    sx={{
                                        fontFamily: "'Inter', sans-serif",
                                        fontSize: "0.72rem",
                                        color: "rgba(255,255,255,0.52)",
                                        lineHeight: 1.4,
                                        mt: 0.3,
                                    }}
                                >
                                    {s.label}
                                </Typography>
                            </StatPill>
                        ))}
                    </IllustrationBox>
                </Card>
            </Container>
        </Section>
    );
};

export default BusinessTeaser;
